import { faChild, faMinus, faPerson, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Checkbox, IconButton, Stack, Typography, Tooltip } from '@vapor/material'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AdditionalServiceFragment, PricingType } from 'src/api/api.generated'
import { Currency } from 'src/api/currency'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { calculateAdditionalServiceQuantity } from 'src/utils/additional-services-utils'

type AdditionalServiceOptionProps = {
  additionalService: AdditionalServiceFragment
  resourceTypeName: string
  price: number
  finalPrice: number
  adults: number
  children?: number
  nights: number
  currency: Currency
  quantity: number | null
  maxQuantity: number | null
  isAdded: boolean
  pricingType: PricingType
  onQuantityChanges: (newQuantity: number) => void
  onAddedChanges: (newIncluded: boolean) => void
}

export default function AdditionalServiceOption(props: AdditionalServiceOptionProps) {
  const { additionalService, resourceTypeName, quantity, maxQuantity, isAdded, price, finalPrice, currency, adults, children, nights, onQuantityChanges, onAddedChanges } = props
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency()
  const showError = useErrorSnackBar()

  const changeQuantity = useCallback(
    (operation: 'INCREASE' | 'DECREASE') => {
      if (operation === 'INCREASE') {
        onQuantityChanges((quantity ?? 0) + 1)
      } else if (quantity) {
        onQuantityChanges(quantity - 1)
      }
    },
    [quantity, onQuantityChanges],
  )

  const minusButtonDisabled = useMemo(() => {
    return quantity === null || quantity <= 0
  }, [quantity])

  const plusButtonDisabled = useMemo(() => {
    return maxQuantity !== null && (quantity ?? 0) >= maxQuantity
  }, [quantity, maxQuantity])

  const showQuantitySelector = useMemo(() => {
    return additionalService.pricingType === PricingType.PER_STAY || additionalService.pricingType === PricingType.PER_NIGHT
  }, [additionalService.pricingType])

  const isAvailable = useMemo(() => {
    // const availability = additionalService.availability

    // if (availability) {
    //   const quantity = calculateAdditionalServiceQuantity({
    //     adults: adults,
    //     children: children ?? 0,
    //     days: nights,
    //     pricingType: additionalService.pricingType,
    //     automaticForAdults: additionalService.automaticForAdults,
    //     automaticForChildren: additionalService.automaticForChildren,
    //     quantity: 1,
    //   })
    //   return quantity < availability
    // }
    return true
  }, [additionalService, adults, children, nights])

  useEffect(() => {
    const hasQuantity = (quantity ?? 0) > 0
    if (showQuantitySelector && hasQuantity !== isAdded) {
      onAddedChanges(hasQuantity)
    }
  }, [showQuantitySelector, quantity])

  useEffect(() => {
    const hasQuantity = (quantity ?? 0) > 0
    if (showQuantitySelector && isAdded && !hasQuantity) {
      onQuantityChanges(1)
    } else if (showQuantitySelector && !isAdded && hasQuantity) {
      onQuantityChanges(0)
    }
  }, [showQuantitySelector, isAdded])

  return (
    <>
      <div style={{ width: '100%' }}>
        <Box
          sx={{ display: 'flex', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}
          onClick={!isAvailable ? () => showError(t('pms.booking.reservation_new.cart.resources_unavailable')) : undefined}
        >
          <Tooltip arrow title={t('pms.booking.reservation_new.add_service_check.tooltip')}>
            <span>
              <Checkbox checked={isAdded} onChange={(event) => onAddedChanges(event.target.checked)} sx={{ pointerEvents: isAvailable ? 'auto' : 'none' }} />
            </span>
          </Tooltip>
          <Stack direction="column" sx={{ flexGrow: 2, marginRight: 2, marginLeft: 1 }}>
            <Stack direction="row" alignItems="flex-end">
              <Typography
                variant="body"
                color={'var(--DarkImperialBlue)'}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical' }}
              >
                <b>{resourceTypeName}</b>
              </Typography>
            </Stack>

            <Box sx={{ display: 'flex', bgcolor: 'background.paper', borderRadius: 1 }}>
              <Stack direction="row">
                <Stack direction="row" alignItems="flex-end">
                  <Typography variant="captionSmall" color="gray">
                    {adults}x
                  </Typography>
                  <Typography>
                    <FontAwesomeIcon icon={faPerson} color={'var(--DarkImperialBlue)'} />
                  </Typography>
                </Stack>

                {children !== undefined && (
                  <Stack direction="row" alignItems="flex-end">
                    <Typography variant="captionSmall" color="gray">
                      {children}x
                    </Typography>
                    <Typography>
                      <FontAwesomeIcon icon={faChild} style={{ height: 13, marginTop: 3 }} color={'var(--DarkImperialBlue)'} />
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {!showQuantitySelector && (
                <>
                  <div style={{ flexGrow: 2 }} />
                  <Typography variant="body">
                    {t('pms.booking.reservation_new.price')} <b>{formatCurrency(finalPrice * (quantity ?? 1), currency)}</b>
                  </Typography>
                </>
              )}
            </Box>
          </Stack>

          {showQuantitySelector && (
            <Stack direction="column" flexShrink={2}>
              <Stack direction="row" alignItems="center">
                <IconButton
                  size="small"
                  disabled={minusButtonDisabled}
                  onClick={() => changeQuantity('DECREASE')}
                  sx={{ pointerEvents: isAvailable ? 'auto' : 'none', backgroundColor: 'rgb(240,240,240)' }}
                >
                  <FontAwesomeIcon icon={faMinus} style={{ width: 15, height: 15 }} />
                </IconButton>
                <Stack direction="row" justifyContent="center" alignItems="center" style={{ width: 31 }}>
                  <Typography variant="body">{quantity ?? 0}</Typography>
                </Stack>
                <IconButton
                  size="small"
                  disabled={plusButtonDisabled}
                  onClick={() => changeQuantity('INCREASE')}
                  sx={{ pointerEvents: isAvailable ? 'auto' : 'none', backgroundColor: 'rgb(240,240,240)' }}
                >
                  <FontAwesomeIcon icon={faPlus} style={{ width: 15, height: 15 }} />
                </IconButton>
              </Stack>
              <Typography variant="body">
                {t('pms.booking.reservation_new.price')} <b>{formatCurrency(finalPrice * (quantity ?? 1), currency)}</b>
              </Typography>
            </Stack>
          )}
        </Box>
      </div>
    </>
  )
}
